import {
  AnswerTarget,
  AnswerTargetSemantic,
  Citation,
  Questionnaire,
  QuestionnaireResponsePair,
  QuestionnaireSection,
  QuestionnaireStatus,
  QuestionType,
} from "@/types/questionnaire/models"
import { useQuery } from "@tanstack/react-query"

export const ONBOARDING_DEMO_QUESTIONNAIRE_ID =
  "00000000-0000-0000-0000-000000000000"

// Note: Excel file paths are now served through the backend API
// The backend streams these files from public S3 URLs when it detects the demo questionnaire ID
// These constants are kept for reference but the actual files are no longer served as static assets
export const ONBOARDING_DEMO_EXCEL_FILE_PATH =
  "/excel-lieferantenauskunft-updated.xlsx"
export const ONBOARDING_DEMO_ANSWERS_EXCEL_PATH =
  "/excel-lieferantenauskunft-updated-beantwortet.xlsx"
export const ONBOARDING_DEMO_JSON_PATH =
  "/excel-lieferantenauskunft-updated.json"
export const ONBOARDING_DEMO_ANSWERS_JSON_PATH =
  "/excel-lieferantenauskunft-updated-answers.json"
export const ONBOARDING_DEMO_UPLOAD_FLAG = "turnus-onboarding-demo-upload"
export const ONBOARDING_DEMO_EXTRACTION_FLAG =
  "onboarding-demo-question-extraction"
export const ONBOARDING_DEMO_QUESTION_ANSWERING_FLAG =
  "onboarding-demo-question-answering"
export const ONBOARDING_UPLOAD_STEP_ID = "upload-file-step"
export const ONBOARDING_EXTRACTION_STEP_ID = "extraction-animation-step"
export const ONBOARDING_QUESTION_ANSWERING_STEP_ID =
  "question-answering-animation-step"
export const ONBOARDING_GENERATE_STEP_ID = "generate-answers-step"
export const ONBOARDING_CELEBRATE_STEP_ID = "celebrate-answer-step"
export const ONBOARDING_FIRST_ANSWER_STEP_ID = "first-answer-step"
export const ONBOARDING_FIRST_ANSWER_SOURCE_STEP_ID = "first-answer-source-step"
export const ONBOARDING_DONE_STEP_ID = "done-answers-step"
export const ONBOARDING_DOWNLOAD_STEP_ID = "download-file-step"

interface RawAnswerTarget {
  addr: string
  label: string | null
  semantics: AnswerTargetSemantic
  allowed_values: string[] | null
}

interface RawCitation {
  source_file_name?: string
  source_file_id?: string
  source_file_page?: number | string
  source_text?: string
}

interface RawResponsePair {
  id: string
  question: string
  response: string | null
  manually_edited?: boolean
  low_confidence?: boolean | null
  citations?: RawCitation[]
  organization: string
  answer_targets_json?: RawAnswerTarget[]
  sheet_name?: string | null
  section_id: string
  questionnaire_id?: string
  type?: string
}

interface RawSection {
  id: string
  name: string
  organization: string
  questionnaire_id?: string
  response_pairs: RawResponsePair[]
}

interface RawQuestionnaire {
  user_id: string
  user_name: string
  file_name: string
  organization: string
  status?: string
  sections: RawSection[]
}

const parseStatus = (status?: string): QuestionnaireStatus => {
  if (!status) {
    return QuestionnaireStatus.Extracted
  }

  const normalized = status.toLowerCase() as QuestionnaireStatus
  if ((Object.values(QuestionnaireStatus) as string[]).includes(normalized)) {
    return normalized
  }

  return QuestionnaireStatus.Extracted
}

const mapAnswerTarget = (target: RawAnswerTarget): AnswerTarget => ({
  addr: target.addr,
  label: target.label,
  semantics: target.semantics,
  allowedValues: target.allowed_values ?? null,
})

const mapCitation = (citation: RawCitation): Citation => ({
  sourceFileName: citation.source_file_name ?? "",
  sourceFileId: citation.source_file_id ?? "",
  sourceFilePage: Number(citation.source_file_page ?? 0),
  sourceText: citation.source_text ?? undefined,
})

const mapResponsePair = (pair: RawResponsePair): QuestionnaireResponsePair => ({
  id: pair.id,
  section: pair.section_id,
  questionnaire: ONBOARDING_DEMO_QUESTIONNAIRE_ID,
  question: pair.question,
  response: pair.response,
  manuallyEdited: Boolean(pair.manually_edited),
  lowConfidence: Boolean(pair.low_confidence),
  citations: (pair.citations ?? []).map(mapCitation),
  organization: pair.organization,
  answerTargetsJson: pair.answer_targets_json
    ? pair.answer_targets_json.map(mapAnswerTarget)
    : null,
  sheetName: pair.sheet_name ?? null,
  type: pair.type as QuestionType | undefined,
})

const mapSection = (section: RawSection): QuestionnaireSection => ({
  id: section.id,
  name: section.name,
  organization: section.organization,
  questionnaire: ONBOARDING_DEMO_QUESTIONNAIRE_ID,
  responsePairs: section.response_pairs.map(mapResponsePair),
})

export const loadOnboardingDemoQuestionnaire = async (overrides?: {
  fileName?: string
  sourcePath?: string
}): Promise<Questionnaire> => {
  const response = await fetch(
    overrides?.sourcePath ?? ONBOARDING_DEMO_JSON_PATH,
  )
  if (!response.ok) {
    throw new Error("Failed to load onboarding demo questionnaire")
  }

  const raw = (await response.json()) as RawQuestionnaire

  return {
    id: ONBOARDING_DEMO_QUESTIONNAIRE_ID,
    userId: raw.user_id,
    userName: raw.user_name,
    fileName: overrides?.fileName ?? raw.file_name,
    errorCode: null,
    errorMessage: "",
    isRetryable: false,
    createdAt: new Date(),
    updatedAt: new Date(),
    organization: raw.organization,
    status: parseStatus(raw.status),
    sections: raw.sections.map(mapSection),
    hasPositionData: true,
  }
}

export const useOnboardingDemoQuestionnaire = (
  enabled: boolean,
  options?: { sourcePath?: string },
) => {
  return useQuery({
    queryKey: [
      "onboarding-demo-questionnaire",
      options?.sourcePath ?? ONBOARDING_DEMO_JSON_PATH,
    ],
    queryFn: () =>
      loadOnboardingDemoQuestionnaire({ sourcePath: options?.sourcePath }),
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
  })
}
